import type { CompanyPlan } from '@core/services/admin-companies.service';

export interface CompanyPlanFeature {
  label: string;
  included: boolean;
}

export interface CompanyPlanOption {
  value: CompanyPlan;
  label: string;
  tagline: string;
  /** Orden de tier, usado para detectar downgrades (menor = plan inferior). */
  tier: number;
  hasAlarms: boolean;
  hasMultiConnection: boolean;
  /** Bases de datos ERP permitidas; null = sin límite. Igual que fallbackErpLimitByPlan en pagos. */
  maxDatabases: number | null;
  features: CompanyPlanFeature[];
}

/**
 * Copy de los mockups de Superadmin · Clientes, ajustado a lo confirmado con
 * producto: Pro incluye Alarmas y multiconexión (hasta 3 BD), como ya dice la
 * pantalla de Pagos. Solo Lite está limitado.
 */
export const COMPANY_PLAN_OPTIONS: CompanyPlanOption[] = [
  {
    value: 'lite',
    label: 'Lite',
    tagline: 'Nivel básico',
    tier: 1,
    hasAlarms: false,
    hasMultiConnection: false,
    maxDatabases: 1,
    features: [
      { label: 'Conexión con a3ERP', included: true },
      { label: '1 base de datos por usuario', included: true },
      { label: 'Multiconexión', included: false },
      { label: 'Alarmas', included: false },
    ],
  },
  {
    value: 'pro',
    label: 'Pro',
    tagline: 'Plan intermedio',
    tier: 2,
    hasAlarms: true,
    hasMultiConnection: true,
    maxDatabases: 3,
    features: [
      { label: 'Conexión con a3ERP', included: true },
      { label: 'Multiconexión (hasta 3 bases de datos)', included: true },
      { label: 'Alarmas', included: true },
    ],
  },
  {
    value: 'premium',
    label: 'Premium',
    tagline: 'Acceso completo',
    tier: 3,
    hasAlarms: true,
    hasMultiConnection: true,
    maxDatabases: null,
    features: [
      { label: 'Todas las funcionalidades', included: true },
      { label: 'Multiconexión de bases de datos', included: true },
      { label: 'Alarmas', included: true },
    ],
  },
];

export const COMPANY_PLAN_BY_VALUE: Record<CompanyPlan, CompanyPlanOption> =
  COMPANY_PLAN_OPTIONS.reduce(
    (acc, plan) => {
      acc[plan.value] = plan;
      return acc;
    },
    {} as Record<CompanyPlan, CompanyPlanOption>,
  );

/** Plan más bajo que incluye Alarmas (para textos tipo "disponible desde el plan X"). */
export const LOWEST_PLAN_WITH_ALARMS: CompanyPlanOption =
  COMPANY_PLAN_OPTIONS.find((plan) => plan.hasAlarms)!;

/** Plan más bajo con multiconexión. */
export const LOWEST_PLAN_WITH_MULTI_CONNECTION: CompanyPlanOption =
  COMPANY_PLAN_OPTIONS.find((plan) => plan.hasMultiConnection)!;

export function isPlanDowngrade(from: CompanyPlan | null, to: CompanyPlan): boolean {
  if (!from) return false;
  return COMPANY_PLAN_BY_VALUE[to].tier < COMPANY_PLAN_BY_VALUE[from].tier;
}

/** Mismas clases de badge que el resto de la plataforma (Premium oscuro, Pro marca, Lite gris). */
export const COMPANY_PLAN_BADGE_CLASSES: Record<CompanyPlan, string> = {
  lite: 'bg-[#F3F4F6] text-[#374151] dark:bg-[var(--color-neutral-700)] dark:text-[var(--color-gray-300)]',
  pro: 'bg-[var(--color-brand-200)] text-[var(--color-brand-500)] dark:bg-[var(--color-brand-400)]/20 dark:text-[var(--color-brand-300)]',
  premium: 'bg-[var(--color-brand-500)] text-white dark:bg-[var(--color-brand-400)]',
};
